import apiClient from './apiClient';

export interface TenantBranding {
  tenantId: string;
  displayName: string | null;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  loginPrimaryColor: string | null;
  loginAccentColor: string | null;
  loginGreeting: string | null;
  supportEmail: string | null;
  themeId: string | null;
  themeConfig: Record<string, unknown> | null;
}

export interface BrandingPatch {
  displayName?: string;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  loginPrimaryColor?: string | null;
  loginAccentColor?: string | null;
  loginGreeting?: string | null;
  supportEmail?: string | null;
  themeId?: string | null;
  themeIdDark?: string | null;
  themeConfig?: Record<string, unknown> | null;
  themeConfigDark?: Record<string, unknown> | null;
  pdfTemplate?: string | null;
  pdfAccentColor?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in (payload as object) &&
    'data' in (payload as object)
  ) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
};

export const tenantBrandingApi = {
  get: async (tenantId: string): Promise<TenantBranding> => {
    const { data } = await apiClient.get<ApiEnvelope<TenantBranding>>(
      `/tenants/${tenantId}/branding`,
    );
    return unwrap(data);
  },

  patch: async (
    tenantId: string,
    patch: BrandingPatch,
  ): Promise<TenantBranding> => {
    const { data } = await apiClient.patch<ApiEnvelope<TenantBranding>>(
      `/tenants/${tenantId}/branding`,
      patch,
    );
    return unwrap(data);
  },

  changeSlug: async (
    tenantId: string,
    slug: string,
  ): Promise<{ id: string; slug: string }> => {
    const { data } = await apiClient.patch<
      ApiEnvelope<{ id: string; slug: string }>
    >(`/tenants/${tenantId}/slug`, { slug });
    return unwrap(data);
  },

  /**
   * Upload a logo image to the public branding bucket. Server returns
   * the canonical CDN URL; caller should `patch()` with the new URL
   * to actually attach it to the tenant.
   *
   * Accepted: PNG, JPEG, WebP, SVG. Max 2 MiB.
   */
  uploadLogo: async (
    tenantId: string,
    file: File,
    kind: 'logo' | 'logoDark' | 'favicon' = 'logo',
  ): Promise<{ url: string; key: string }> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<ApiEnvelope<{ url: string; key: string }>>(
      `/tenants/${tenantId}/branding/logo`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { kind },
      },
    );
    return unwrap(data);
  },

  /**
   * AI brand-theme recommender. Returns 2–3 {primary, accent} seed
   * suggestions; the caller builds full light + dark themes from each via
   * buildThemePairFromSeed. At least one of seedColors / logoUrl /
   * description should be provided.
   */
  suggestTheme: async (
    tenantId: string,
    payload: {
      seedColors?: string[];
      logoUrl?: string;
      description?: string;
    },
  ): Promise<ThemeSuggestion[]> => {
    const { data } = await apiClient.post<ApiEnvelope<ThemeSuggestion[]>>(
      `/tenants/${tenantId}/branding/ai/suggest-theme`,
      payload,
    );
    return unwrap(data);
  },
};

export type ThemeSurface = 'cool' | 'warm' | 'neutral' | 'deep';

export interface ThemeSuggestion {
  name: string;
  primary: string;
  accent: string;
  rationale: string;
  /** Surface/neutral tone for the dark palette. */
  surface?: ThemeSurface;
  /** Brand-tuned semantic colours (#rrggbb); FE defaults if absent. */
  success?: string;
  warning?: string;
  error?: string;
  /** Sign-in hero colours; default to primary/accent. */
  loginPrimary?: string;
  loginAccent?: string;
}
